/* Gelände formen: Eckpunkte in einem Kreis anheben, absenken oder glätten (wie das Sculpt-Werkzeug der Source-Editoren).
 * Wirkt auf alle Brushes der Welt und von func_detail, nicht nur auf Dreiecks-Prismen: Eckpunkte an gleicher Stelle (gemeinsame Kante oder Ecke) bilden eine Gruppe
 * und wandern gemeinsam, darum entstehen keine Spalten zwischen Nachbarn. Es bewegen sich nur Eckpunkte, die an einer nach oben zeigenden Fläche liegen;
 * Böden, Rückseiten und alles darunter bleiben. Jeder berührte Brush wird danach als konvexe Hülle seiner Eckpunkte neu gebaut, ist also immer gültig;
 * eine Fläche, die dabei windschief würde, teilt die Hülle in Dreiecke. Boden = Richtung +z, Felswand = ±x/±y: je Richtung wird das Brush-Set so gedreht
 * (reine 90°-Drehung, Umlaufsinn bleibt), dass die Oberfläche nach oben zeigt, dort geformt und zurückgedreht. Ohne DOM. */
import type { Brush, Doc, Entity, Sel, Vec3 } from './types.ts';
import { buildHull, faceNormal } from './brush.ts';
import { isProtected, validBrush } from './simplify.ts';

export type SculptMode = 'push' | 'smooth';
/** Richtung, in die die Oberfläche eines Prismas zeigt: 0 +z (Boden), 1 −z, 2 +x, 3 −x, 4 +y, 5 −y */
export type Dir = 0 | 1 | 2 | 3 | 4 | 5;
export const DIRS: Dir[] = [0, 1, 2, 3, 4, 5];
/** Zeilen der Drehmatrizen (det = 1), die die Richtung auf +z drehen */
const ROT: number[][][] = [
  [[1, 0, 0], [0, 1, 0], [0, 0, 1]],
  [[1, 0, 0], [0, -1, 0], [0, 0, -1]],
  [[0, 0, -1], [0, 1, 0], [1, 0, 0]],
  [[0, 0, 1], [0, 1, 0], [-1, 0, 0]],
  [[1, 0, 0], [0, 0, -1], [0, 1, 0]],
  [[1, 0, 0], [0, 0, 1], [0, -1, 0]]
];
const turn = (m: number[][], v: Vec3): Vec3 => [m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2], m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2], m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2]];
const flip = (m: number[][]): number[][] => [[m[0][0], m[1][0], m[2][0]], [m[0][1], m[1][1], m[2][1]], [m[0][2], m[1][2], m[2][2]]];
/** Brush in das Bezugssystem der Richtung drehen (Eckpunkte und Texturachsen); gleiche Id */
function turnBrush(b: Brush, m: number[][]): Brush {
  const out: Brush = { id: b.id, verts: b.verts.map((v) => turn(m, v)), faces: b.faces.map((f) => ({ ...f, ua: turn(m, f.ua), va: turn(m, f.va) })) };
  return out;
}
export interface SculptOpts {
  /** Mitte des Pinsels in Karten-Koordinaten */
  cx: number; cy: number;
  /** Richtung der Oberfläche (Standard 0 = Boden); cz ist dann die dritte Koordinate der Mitte */
  dir?: Dir;
  /** Radius des Pinsels (Einheiten) */
  radius: number;
  /** push: > 0 hebt an, < 0 drückt ein (Höhe in der Mitte); smooth: 0..1 Anteil, der zum Mittel der Nachbarn wandert */
  strength: number;
  mode: SculptMode;
  /** Brush, auf den geklickt wurde: es bewegt sich nur die zusammenhängende Oberfläche, die von ihm ausgeht (Kanten nach oben zeigender Flächen);
   *  andere Flächen in Reichweite (darunter, daneben ohne Verbindung, andere Höhe) bleiben. Ohne from zählt nur die Höhe (cz ± zRange). */
  from?: string;
  /** nur Eckpunkte in Tiefe cz ± zRange formen (übereinanderliegende Flächen bleiben getrennt); ohne cz alle. Bei dir 0 ist cz die Höhe, sonst die Koordinate der dritten Achse */
  cz?: number; zRange?: number;
}
export const DEFAULTS: SculptOpts = { cx: 0, cy: 0, radius: 256, strength: 16, mode: 'push' };
/** Mindestdicke zwischen Oberseite und Boden */
export const MIN_THICK = 16;

export interface SculptResult { doc: Doc; sel: Sel; moved: number; brushes: number; limited: number; rejected: number }

/** Kubischer Abfall: 1 in der Mitte, 0 am Rand, weich (3t² − 2t³) */
export function falloff(d: number, r: number): number {
  if (!(r > 0) || d >= r) return 0;
  const t = 1 - d / r;
  return t * t * (3 - 2 * t);
}

const sculptable = (e: Entity): boolean => e.kv.classname === 'worldspawn' || e.kv.classname === 'func_detail';

/** Ids der Brushes, die das Werkzeug formen kann (alle Brushes der Welt und von func_detail außer Spezialtexturen wie Himmel und Wasser);
 *  der Pinsel zielt nur auf diese, nicht auf Trigger oder andere Entities davor */
export function sculptableIds(doc: Doc): Set<string> {
  const out = new Set<string>();
  for (const e of doc.ents) if (sculptable(e)) for (const b of e.brushes) if (!isProtected(b)) out.add(b.id);
  return out;
}

const AXIS: Vec3[] = [[0, 0, 1], [0, 0, -1], [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0]];
/** Richtung, in die die getroffene Fläche (Normale n) am ehesten zeigt */
export function pickDir(n: Vec3 | null): Dir {
  if (!n) return 0;
  let best: Dir = 0, bs = -Infinity;
  for (const d of DIRS) { const a = AXIS[d], sc = a[0] * n[0] + a[1] * n[1] + a[2] * n[2]; if (sc > bs) { bs = sc; best = d; } }
  return best;
}

const vkey = (v: Vec3): string => Math.round(v[0] * 100) + ',' + Math.round(v[1] * 100) + ',' + Math.round(v[2] * 100);

/** Vorbereitung für eine Richtung: Eckpunktgruppen, Nachbarn, Untergrenzen. Hängt nur vom Dokument ab (nicht vom Pinsel), darum für die Vorschau zwischenspeicherbar. */
export interface Prep {
  dir: Dir; M: number[][]; Mi: number[][];
  owner: Map<string, Entity>; pool: Brush[];
  /** Gruppe je Eckpunkt je Brush, Position je Gruppe (im Bezugssystem der Richtung) */
  bg: number[][]; gv: Vec3[];
  eligible: boolean[]; bound: number[]; nb: Map<number, Set<number>>;
  /** Brushes je Gruppe; nach oben zeigende Flächen je Brush (Indizes) */
  gb: number[][]; up: number[][];
  /** Brush-Id → Index im Pool; Kanten nur nach oben zeigender Flächen (für den Zusammenhang der Oberfläche) */
  idx: Map<string, number>; nbUp: Map<number, Set<number>>;
  /** Ausschnitt (Mitte im Bezugssystem der Richtung), falls nur ein Teil der Brushes vorbereitet wurde */
  win?: { c: Vec3; r: number };
}

/** Ausschnitt für prepare: nur Brushes, die den Kreis um (cx, cy, cz) mit Radius r berühren. cz wie in SculptOpts (ohne: 0). */
export interface Window { cx: number; cy: number; cz?: number; r: number }

/** Umgebender Quader je Brush, einmal berechnet (Brushes sind unveränderlich, ein neu gebauter Brush ist ein neues Objekt) */
const BOX = new WeakMap<Brush, number[]>();
function boxOf(b: Brush): number[] {
  let x = BOX.get(b);
  if (!x) {
    x = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
    for (const v of b.verts) for (let k = 0; k < 3; k++) { if (v[k] < x[k]) x[k] = v[k]; if (v[k] > x[k + 3]) x[k + 3] = v[k]; }
    BOX.set(b, x);
  }
  return x;
}

/** Berührt der Quader des Brushes (im Bezugssystem der Richtung) den Kreis c, r in der Draufsicht? */
function touches(b: Brush, M: number[][], c: Vec3, r: number): boolean {
  const x = boxOf(b), p = turn(M, [x[0], x[1], x[2]]), q = turn(M, [x[3], x[4], x[5]]);
  const dx = Math.max(Math.min(p[0], q[0]) - c[0], 0, c[0] - Math.max(p[0], q[0]));
  const dy = Math.max(Math.min(p[1], q[1]) - c[1], 0, c[1] - Math.max(p[1], q[1]));
  return dx * dx + dy * dy <= r * r;
}

/** Deckt die Vorbereitung den Pinsel ab? (Ohne Ausschnitt immer.) Liegt der Kreis des Pinsels ganz im Ausschnitt, ist das Ergebnis dasselbe wie mit allen Brushes,
 *  bis auf eine Ausnahme: Flächen, die nur über einen Umweg außerhalb des Ausschnitts zusammenhängen, zählen dann als getrennt. */
export function covers(P: Prep, o: SculptOpts): boolean {
  if (!P.win) return true;
  const c = turn(P.M, [Number(o.cx), Number(o.cy), Number(o.cz ?? 0)]);
  return Math.hypot(c[0] - P.win.c[0], c[1] - P.win.c[1]) + Number(o.radius) <= P.win.r;
}

export function prepare(doc: Doc, sel: Sel | null, dir: Dir = 0, win?: Window): Prep {
  const M = ROT[dir], Mi = flip(M), only = sel && sel.b.size ? sel.b : null;
  const owner = new Map<string, Entity>(), pool: Brush[] = [];
  const wc = win ? turn(M, [win.cx, win.cy, win.cz ?? 0]) : null;
  for (const e of doc.ents) if (sculptable(e)) for (const b of e.brushes) {
    if ((only && !only.has(b.id)) || (win && wc && !touches(b, M, wc, win.r)) || isProtected(b)) continue;
    owner.set(b.id, e);
    pool.push(dir === 0 ? b : turnBrush(b, M));
  }
  const gid = new Map<string, number>(), gv: Vec3[] = [];
  const bg: number[][] = pool.map((b) => b.verts.map((v) => {
    const k = vkey(v);
    let g = gid.get(k);
    if (g === undefined) { g = gv.length; gid.set(k, g); gv.push([Math.round(v[0] * 100) / 100, Math.round(v[1] * 100) / 100, Math.round(v[2] * 100) / 100]); }
    return g;
  }));
  const eligible = new Array<boolean>(gv.length).fill(false), bound = new Array<number>(gv.length).fill(-Infinity);
  const nb = new Map<number, Set<number>>(), nbUp = new Map<number, Set<number>>(), gb: number[][] = gv.map(() => []), up: number[][] = [];
  pool.forEach((b, bi) => {
    const lowest = Math.min(...b.verts.map((v) => v[2])), mine: number[] = [];
    b.faces.forEach((f, fi) => {
      const isUp = faceNormal(b.verts, f.vi)[2] > 0.05;
      if (isUp) mine.push(fi);
      f.vi.forEach((vi, k) => {
        const g = bg[bi][vi], h = bg[bi][f.vi[(k + 1) % f.vi.length]];
        if (isUp) { eligible[g] = true; (nbUp.get(g) || nbUp.set(g, new Set()).get(g) as Set<number>).add(h); (nbUp.get(h) || nbUp.set(h, new Set()).get(h) as Set<number>).add(g); }
        (nb.get(g) || nb.set(g, new Set()).get(g) as Set<number>).add(h);
      });
    });
    up.push(mine);
    // Mindestdicke über dem tiefsten Punkt jedes Brushes, der den Eckpunkt trägt
    b.verts.forEach((v, vi) => { const g = bg[bi][vi]; bound[g] = Math.max(bound[g], Math.min(lowest + MIN_THICK, v[2])); gb[g].push(bi); });
  });
  const idx = new Map<string, number>(pool.map((b, i) => [b.id, i]));
  return { dir, M, Mi, owner, pool, bg, gv, eligible, bound, nb, gb, up, idx, nbUp, win: win && wc ? { c: wc, r: win.r } : undefined };
}

/** Oberfläche unter einem Punkt (für Aufrufe ohne Mausklick, z. B. `xf sculpt`): der Brush, dessen nach oben zeigende Fläche (x, y) überdeckt, und der Punkt darauf.
 *  p in Karten-Koordinaten; ohne dritten Wert gewinnt die höchste Fläche (Boden: von oben gesehen), mit ihm die Fläche, die dem Wert am nächsten liegt. */
export function surfaceAt(P: Prep, p: number[]): { id: string; pos: Vec3 } | null {
  const c = turn(P.M, [p[0], p[1], p[2] ?? 0]), wanted = p[2] !== undefined && Number.isFinite(p[2]);
  let best: { id: string; z: number; score: number } | null = null;
  P.pool.forEach((b, bi) => {
    for (const fi of P.up[bi]) {
      const vi = b.faces[fi].vi, V = vi.map((i) => b.verts[i]);
      // Punkt im konvexen Vieleck (Draufsicht im Bezugssystem)
      let side = 0, inside = true;
      for (let k = 0; k < V.length && inside; k++) {
        const a = V[k], d = V[(k + 1) % V.length], cr = (d[0] - a[0]) * (c[1] - a[1]) - (d[1] - a[1]) * (c[0] - a[0]);
        if (Math.abs(cr) < 1e-9) continue;
        if (side === 0) side = Math.sign(cr); else if (Math.sign(cr) !== side) inside = false;
      }
      if (!inside) continue;
      const n = faceNormal(b.verts, vi);
      if (n[2] <= 0.05) continue;
      const z = (n[0] * V[0][0] + n[1] * V[0][1] + n[2] * V[0][2] - n[0] * c[0] - n[1] * c[1]) / n[2];
      const score = wanted ? -Math.abs(z - c[2]) : z;
      if (!best || score > best.score) best = { id: b.id, z, score };
    }
  });
  return best ? { id: (best as { id: string }).id, pos: turn(P.Mi, [c[0], c[1], (best as { z: number }).z]) } : null;
}

/** Neue Höhe je betroffener Eckpunktgruppe (nur Rechnung, keine Brushes) */
export function displace(P: Prep, o: SculptOpts): { nz: Map<number, number>; limited: number } {
  const nz = new Map<number, number>();
  let limited = 0;
  if (!(o.radius > 0) || !o.strength) return { nz, limited };
  const c = turn(P.M, [o.cx, o.cy, o.cz ?? 0]);
  // zusammenhängende Oberfläche ab dem getroffenen Brush: über Kanten nach oben zeigender Flächen, daher nie durch Stufen, Wände oder darunterliegende Schichten
  let reach: Set<number> | null = null;
  if (o.from !== undefined) {
    reach = new Set<number>();
    const bi = P.idx.get(o.from);
    if (bi === undefined) return { nz, limited };
    const queue: number[] = [];
    for (const fi of P.up[bi]) for (const vi of P.pool[bi].faces[fi].vi) { const g = P.bg[bi][vi]; if (!reach.has(g)) { reach.add(g); queue.push(g); } }
    for (let q = 0; q < queue.length; q++) for (const h of P.nbUp.get(queue[q]) || []) if (!reach.has(h)) { reach.add(h); queue.push(h); }
  }
  P.gv.forEach((p, g) => {
    if (!P.eligible[g] || (reach && !reach.has(g))) return;
    const w = falloff(Math.hypot(p[0] - c[0], p[1] - c[1]), o.radius);
    if (w <= 0) return;
    if (!reach && o.cz !== undefined && Math.abs(p[2] - c[2]) > (o.zRange ?? o.radius)) return;
    let z: number;
    if (o.mode === 'smooth') {
      const ns = [...(P.nb.get(g) || [])].filter((h) => P.eligible[h]);
      if (!ns.length) return;
      z = p[2] + (ns.reduce((a, h) => a + P.gv[h][2], 0) / ns.length - p[2]) * Math.max(0, Math.min(1, o.strength)) * w;
    } else z = p[2] + o.strength * w;
    z = Math.round(z);
    if (z < P.bound[g]) { z = Math.round(P.bound[g]); limited++; }
    if (z !== p[2]) nz.set(g, z);
  });
  return { nz, limited };
}

/** Vorschau ohne Brush-Neubau: die Kanten der nach oben zeigenden Flächen aller berührten Brushes mit den neuen Höhen,
 *  als Linienpaare in Karten-Koordinaten [x0,y0,z0,x1,y1,z1, …]. Kosten: Eckpunkte im Pinsel plus deren Brushes. */
export function previewLines(P: Prep, nz: Map<number, number>): number[] {
  const out: number[] = [], seen = new Set<string>(), done = new Set<number>();
  const pos = (g: number): Vec3 => { const q = P.gv[g]; return turn(P.Mi, [q[0], q[1], nz.get(g) ?? q[2]]); };
  for (const g0 of nz.keys()) for (const bi of P.gb[g0]) {
    if (done.has(bi)) continue;
    done.add(bi);
    const b = P.pool[bi];
    for (const fi of P.up[bi]) {
      const vi = b.faces[fi].vi;
      for (let k = 0; k < vi.length; k++) {
        const a = P.bg[bi][vi[k]], c = P.bg[bi][vi[(k + 1) % vi.length]], key = a < c ? a + '_' + c : c + '_' + a;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(...pos(a), ...pos(c));
      }
    }
  }
  return out;
}

/** prep: schon berechnete Vorbereitung für genau dieses doc/sel/dir (spart die zweite Rechnung, wenn die Vorschau sie ohnehin hat) */
export function sculpt(doc: Doc, sel: Sel | null, o: SculptOpts, prep?: Prep): SculptResult {
  const dir: Dir = o.dir ?? 0;
  const none: SculptResult = { doc, sel: sel || { b: new Set<string>(), e: new Set<string>() }, moved: 0, brushes: 0, limited: 0, rejected: 0 };
  // ohne mitgegebene Vorbereitung nur Brushes in Pinselnähe; findet das nichts, einmal mit allen Brushes (Flächen, die nur über einen Umweg hängen)
  let P = prep && prep.dir === dir && covers(prep, o) ? prep : prepare(doc, sel, dir, { cx: o.cx, cy: o.cy, cz: o.cz, r: o.radius });
  let r0 = displace(P, o);
  if (!r0.nz.size && P.win) { P = prepare(doc, sel, dir); r0 = displace(P, o); }
  const { nz, limited } = r0;
  if (!nz.size) return { ...none, limited };
  const { pool, bg, gv, owner, Mi } = P;
  // berührte Brushes als konvexe Hülle der verschobenen Eckpunkte neu bauen; jeder Eckpunkt muss Eckpunkt bleiben, sonst entstünde ein Spalt
  const made2 = new Map<Entity, Brush[]>(), madeIds = new Set<string>();
  for (const e of doc.ents) if (sculptable(e)) made2.set(e, []);
  let rejected = 0;
  const rebuilt: { b: Brush; id: string }[] = [];
  for (let bi = 0; bi < pool.length; bi++) {
    if (!bg[bi].some((g) => nz.has(g))) continue;
    const old = pool[bi];
    const pts = old.verts.map((_, vi): Vec3 => { const g = bg[bi][vi], q = gv[g]; return [q[0], q[1], nz.get(g) ?? q[2]]; });
    const h = buildHull(pts, 0.02);
    const uniq = new Set(pts.map(vkey)).size;
    if (!h || h.verts.length !== uniq) { rejected++; continue; }
    const oldN = old.faces.map((f) => faceNormal(old.verts, f.vi));
    const faces = h.loops.map((vi) => {
      const nn = faceNormal(h.verts, vi);
      let best = 0, bd = -2;
      oldN.forEach((n0, i) => { const d = n0[0] * nn[0] + n0[1] * nn[1] + n0[2] * nn[2]; if (d > bd) { bd = d; best = i; } });
      return { ...old.faces[best], vi };
    });
    const nb2: Brush = { id: old.id, verts: h.verts, faces };
    if (!validBrush(nb2)) { rejected++; continue; }
    rebuilt.push({ b: dir === 0 ? nb2 : turnBrush(nb2, Mi), id: old.id });
  }
  if (rejected) return { ...none, limited, rejected };   // lieber nichts ändern als Spalten zu riskieren
  for (const r of rebuilt) { const e = owner.get(r.id); if (e) { (made2.get(e) as Brush[]).push(r.b); madeIds.add(r.id); } }
  const ents = doc.ents.map((e) => {
    const add = made2.get(e);
    if (!add || !add.length) return e;
    const byId = new Map(add.map((b) => [b.id, b]));
    return { ...e, brushes: e.brushes.map((b) => byId.get(b.id) || b) };
  });
  return { doc: { ents }, sel: { b: madeIds, e: new Set<string>() }, moved: nz.size, brushes: rebuilt.length, limited, rejected: 0 };
}
